import styled from '@emotion/styled'
import { memo, useMemo } from 'react'
import type { AvailableSpaceContentType } from '../../../../../types'
import { SettingsType } from '../../../../constants/enums'
import { RoomView } from '../../../../state/modals'
import { AppStateType, useAppStateSelector } from '../../../Hooks/useAppState'
import { useSettingSelector } from '../../../Hooks/useSettings'
import { useUserSelector } from '../../../Hooks/useUser'
import { LoadingData } from '../../../common/LoadingData'
import { COMPONENT_HEIGHTS } from '../../../theme'
import { Profile } from '../../Profile'
import Whiteboard from '../../Whiteboard'
import ExpandButton from './ExpandButton'
import ModelGrid from './ModelGrid'
import ModelsYouMayLike from './ModelsYouMayLike'
import PrivateMessage from './PrivateMessage'
import Room from './Room'
import SelectorToolbar from './SelectorToolbar'

const StyledRoot = styled('div')({
    position: 'relative',
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
})
const StyledSelectorToolbarRoot = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    height: COMPONENT_HEIGHTS.availableSpaceControlBar,
    minHeight: COMPONENT_HEIGHTS.availableSpaceControlBar,
})
export const StyledContentRoot = styled('div')({
    // ...NO_SCROLLBAR_STYLES,
    overflowY: 'auto',
})

interface IAvailableSpace {
    id: number
    height: number
    width: number
    loading: boolean
    roomView: RoomView
    isLounge?: boolean
    onToggleWhiteboard?(): void
    // pm?: boolean
}

const AvailableSpace = memo((props: IAvailableSpace) => {
    const authenticated = useAppStateSelector(AppStateType.APP, 'authenticated')
    const [availableSpaceView] = useSettingSelector(
        SettingsType.ROOM,
        'availableSpaceView',
    )
    const {
        onToggleWhiteboard,
        height: totalHeight,
        width,
        isLounge,
        loading,
        roomView,
        id,
    } = props
    const name = useUserSelector(id, 'name')

    const height =
        totalHeight > COMPONENT_HEIGHTS.availableSpaceControlBar
            ? totalHeight - COMPONENT_HEIGHTS.availableSpaceControlBar
            : 0

    const inPM = roomView === RoomView.PM
    const inRoom = roomView === RoomView.Room
    const inProfile = roomView === RoomView.Profile

    const hidePmButton = inPM || isLounge || !authenticated
    const hideRoomButton = inRoom
    const hideProfileButton = inProfile
    const hide = useMemo(() => {
        const hiddenButtons = new Set<AvailableSpaceContentType>()
        if (hidePmButton) hiddenButtons.add('pm')
        if (hideRoomButton) hiddenButtons.add('room')
        if (hideProfileButton) hiddenButtons.add('profile')
        return hiddenButtons
    }, [hidePmButton, hideRoomButton, hideProfileButton])

    // shift to avoid duplicating views when someone navigates
    // (ex: user in room, has pm on available space and then navigates to pm from room)
    const shouldChangePM = inPM && availableSpaceView === 'pm'
    const shouldChangeRoom = inRoom && availableSpaceView === 'room'
    const shouldChangeProfile = inProfile && availableSpaceView === 'profile'

    const contentType = shouldChangePM
        ? 'profile'
        : shouldChangeRoom
          ? 'profile'
          : shouldChangeProfile
            ? 'none'
            : availableSpaceView

    const showPM = contentType === 'pm'
    const showRoom = contentType === 'room'
    const showProfile = contentType === 'profile'

    const showMYML = availableSpaceView === 'myml'
    const showModels = availableSpaceView === 'models'
    const showWhiteboard = availableSpaceView === 'whiteboard'

    if (loading) return <LoadingData active showReloadNotice />

    return (
        <StyledRoot data-testid="Root__AvailableSpaceContainer">
            <StyledSelectorToolbarRoot>
                <SelectorToolbar
                    contentType={contentType}
                    hide={hide}
                    modelName={name}
                />
            </StyledSelectorToolbarRoot>
            {!inProfile && <ExpandButton />}
            {showModels && (
                <ModelGrid width={width} activeRoom={id} height={height} />
            )}
            {showPM && <PrivateMessage id={id} height={height} />}
            {showRoom && (
                <Room
                    id={id}
                    height={height}
                    onToggleWhiteboard={onToggleWhiteboard}
                />
            )}
            {showMYML && <ModelsYouMayLike height={height} id={id} inRoom />}
            {showProfile && (
                <StyledContentRoot className="no-scrollbars" style={{ height }}>
                    <Profile slugOrId={id} embedded />
                </StyledContentRoot>
            )}
            {showWhiteboard && (
                <StyledContentRoot className="no-scrollbars" style={{ height }}>
                    <Whiteboard
                        identifier={id}
                        loading={false}
                        showAvatar={false}
                        hideTipButton
                    />
                </StyledContentRoot>
            )}
        </StyledRoot>
    )
})

export default AvailableSpace
